import { Col, Row } from "antd";
import moment from "moment";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCognitoUserData } from "../../../features/auth/authSlice";
import {
  fetchCompanies,
  selectCompanies,
  selectCompaniesAreFetching,
} from "../../../features/company/companySlice";
import {
  fetchGroups,
  selectAreGroupsFetching,
  selectGroupsDict,
} from "../../../features/groups/groupSlice";
import {
  fetchUserStats,
  selectUserStats,
  selectUserStatsAreFetching,
} from "../../../features/reports/company/userStats";
import {
  fetchUsersSessionDuration,
  fetchUsersSessionDurationAll,
  selectUsersSessionDuration,
  selectUsersSessionDurationAll,
  selectUsersSessionDurationIsFetching,
} from "../../../features/reports/users/sessionDuration";
import { GenderDict, UserRole } from "../../../features/users/usersSlice";
import { RoundedContainer } from "../../common/RoundedContainer";
import { AnalyticsViewReport } from "../common/AnalyticsViewReport";
import { ReportTypes, sectionTitles } from "../MenuAndRoutes";
import { CompanyTop5 } from "./CompanyTop5";
import { UserDoughnut } from "./UserDoughnut";
import { UsersStateChart } from "./UsersStateChart";
import { UsersTable } from "./UsersTable";
import { UserStatsChart } from "./UserStatsChart";
import { getTranslations } from "../../../features/translations/translationsUtils";
import { getSelectorsAndSetters } from "../common/interfaces";
// import { getSelectorsAndSetters } from "../common/interfaces";

export enum UserReportType {
  Student = "Student",
  CompanyAdmin = "CompanyAdmin",
  Supervisor = "Supervisor",
}

export enum UserReportRoleDict {
  Student = "REPORT_ROLE_STUDENT",
  CompanyAdmin = "REPORT_COMPANY_ADMIN",
  Supervisor = "REPORT_SUPERVISOR",
}

const UserReportTypeDict = {
  [UserRole.CompanyAdmin]: UserReportType.CompanyAdmin,
  [UserRole.Supervisor]: UserReportType.Supervisor,
};

export interface UserReportData {
  id: string;
  enabled: boolean;
  active: boolean;
  sessionDuration: number;
  groupName: string;
  groupId: string;
  companyId: string;
  companyName: string;
  gender: string;
  genderName: string;
  age: number;
  userType: UserReportType;
  userTypeName: string;
  userName: string;
  email: string;
  phone: string;
  cityName: string;
  countryName: string;
  position: string;
  registrationDate: string;
  lastLoginDate: string;
  status: string;
  activeLabel: string;
  progress: string;
  courses: number;
  finishedCourses: number;
  timeOfUse: string;
  averageScore: string;
  certificates: number;
  badges: number;
}

const secondsToString = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const hoursStr = hours > 0 ? hours.toString().padStart(2, "0") : "00";
  const minutesStr = minutes > 0 ? minutes.toString().padStart(2, "0") : "00";

  return `${hoursStr}:${minutesStr}` || "00:00";
}
interface UserReportsProps {
  companyId: string;
}

export const UsersReports: FunctionComponent<UserReportsProps> = ({
  companyId,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const gaUserSessions = useSelector(selectUsersSessionDuration);
  const gaUserSessionsAll = useSelector(selectUsersSessionDurationAll);
  const companyDict = useSelector(selectCompanies);
  const groupsDict = useSelector(selectGroupsDict);
  const userStats = useSelector(selectUserStats);
  const isFetchingGroups = useSelector(selectAreGroupsFetching);
  const isFetchingCompanies = useSelector(selectCompaniesAreFetching);
  const isFetchinSessions = useSelector(selectUsersSessionDurationIsFetching);
  const isFetchingUserStats = useSelector(selectUserStatsAreFetching);
  const cognitoData = useSelector(getCognitoUserData);
  const role = cognitoData ? cognitoData["custom:role"] : undefined;
  const userCompanyId = cognitoData ? cognitoData["custom:company"] : undefined;
  const userListFilterSelector = getSelectorsAndSetters(ReportTypes.Users)?.selectFilter;
  const selectorAndFilter = useSelector(userListFilterSelector ?? (() => null) );

  const currentCompanyId =
    role === UserRole.SuperAdmin ? companyId : userCompanyId;

  const isLoading =
    isFetchingCompanies ||
    isFetchinSessions ||
    isFetchingGroups ||
    isFetchingCompanies;
  const dispatch = useDispatch();

  useEffect(() => {
    const loadStatsData = async () => {
      dispatch(fetchGroups());
      dispatch(fetchCompanies());
      await dispatch(fetchUsersSessionDuration());
      await dispatch(fetchUsersSessionDurationAll());
      setIsLoaded(true);
    };
    loadStatsData();
  }, [dispatch]);

  useEffect(() => {
    if (isLoaded) {
      dispatch(
        fetchUserStats({
          gausers: gaUserSessions.filter((user) => user.companyId === currentCompanyId).map((user) => ({
            id: user.userId,
            session: user.session,
          })),
        }, currentCompanyId)
      );
    }
  }, [dispatch, gaUserSessions, isLoaded, currentCompanyId]);

  const userReportList: UserReportData[] = userStats.users
    .filter((user) => user.role !== UserRole.SuperAdmin)
    .map((user) => {
      const activeUser = gaUserSessions.find(
        (gaUser) => user.username === gaUser.userId
      );
      const currentUser = user;
      const groupId = currentUser.studentGroupId ?? "";
      const userType: UserReportType[] =
        currentUser.role && currentUser.role !== UserRole.SuperAdmin
          ? [UserReportTypeDict[currentUser.role]]
          : [];

      let userTypeName: string[] = userType.map(userType => getTranslations(UserReportRoleDict[userType]));
      if (
        currentUser.studentGroupId
      ) {
        userTypeName.push(getTranslations(UserReportRoleDict[UserReportType.Student]));
      }
      const gaUserSession = gaUserSessionsAll.find((session) => session.userId === user.username);

      return {
        id: user.username,
        userName: `${currentUser.given_name} ${currentUser.family_name}`,
        companyName: companyDict[currentUser.company ?? ""]?.name,
        enabled: currentUser.enabled,
        active: activeUser !== undefined,
        sessionDuration: activeUser?.session ?? 0,
        groupName: groupsDict[groupId]?.name ?? "",
        groupId,
        companyId: currentUser.company ?? "",
        gender: currentUser.gender ?? "",
        genderName: currentUser.gender ? GenderDict()[currentUser.gender] : "",
        age: moment().diff(
          moment(currentUser.birthdate, "YYYY-MM-DD"),
          "years"
        ),
        userType: userType[0],
        userTypeName: userTypeName.join(', '),
        email: currentUser.email,
        phone: currentUser.phone_number,
        cityName: currentUser.city ?? "",
        countryName: currentUser.country ?? "",
        position: currentUser.position ?? "",
        registrationDate: moment(currentUser.createdAt).format("YYYY-MM-DD"),
        lastLoginDate: currentUser.lastLoginDate
          ? moment(currentUser.lastLoginDate).format("YYYY-MM-DD")
          : "",
        status: currentUser.status === 'FORCE_CHANGE_PASSWORD' ? 'No registrado' : 'Registrado',
        activeLabel: currentUser.enabled ? 'Habilitado' : 'Deshabilitado',
        progress: currentUser.progress ? (currentUser.progress * 100).toFixed(2) + '%' : '0%',
        courses: currentUser.coursesAssigned ?? 0,
        finishedCourses: currentUser.coursesCompleted ?? 0,
        timeOfUse: gaUserSession?.session ? secondsToString(gaUserSession.session) : "00:00",
        averageScore: ((currentUser.averageScore ?? 0) * 100).toString() + '%',
        certificates: currentUser.certificates ?? 0,
        badges: currentUser.badges ?? 0,
      };
    });

  const filteredUserReportList = currentCompanyId
    ? userReportList.filter((user) => {
      // if (user.groupId && filter?.groupId && user.groupId !== filter.groupId) {
      //   return false;
      // }
      if (selectorAndFilter?.groupId) {
        return user.groupId === selectorAndFilter.groupId && user.companyId === currentCompanyId;
      }
      return user.companyId === currentCompanyId;
    })
    : userReportList;
    const activeUsers = filteredUserReportList.filter((user) => user.active && user.enabled);
  const enabledUsers = filteredUserReportList.filter((user) => user.enabled);

  return (
    <Row gutter={[24, 24]} style={{ marginBottom: 46 }}>
      <AnalyticsViewReport
        companyId={currentCompanyId}
        reportName={sectionTitles()[ReportTypes.Users]}
      />
      <Col span={16}>
        <RoundedContainer>
          <UserStatsChart
            registerUsersAmount={filteredUserReportList.length}
            enabledUsersAmount={enabledUsers.length}
            activeUsers={activeUsers}
          />
        </RoundedContainer>
      </Col>
      <Col span={8}>
        <RoundedContainer>
          <UserDoughnut enabledUsers={enabledUsers} activeUsers={activeUsers} />
        </RoundedContainer>
      </Col>
      <Col span={12}>
        <RoundedContainer>
          <UsersStateChart
            enabledUsers={enabledUsers}
            companyId={currentCompanyId}
            isLoading={isLoading || isFetchingUserStats}
          />
        </RoundedContainer>
      </Col>
      <Col span={12}>
        <RoundedContainer>
          <CompanyTop5
            topFive={userStats.topFive}
            isLoading={isLoading || isFetchingUserStats}
          />
        </RoundedContainer>
      </Col>
      <Col span={24}>
        <UsersTable
          companyId={currentCompanyId}
          isLoading={isLoading}
          filteredUserReportList={filteredUserReportList}
        />
      </Col>
    </Row>
  );
};
